/**
 * 🌍 TIMEZONE UTILITY - Frontend
 * 
 * Utility para detectar zona horaria del usuario y normalizar fechas
 * entre frontend y backend.
 * 
 * NOTE: This module now uses DateTimeUtils internally for all operations.
 * It maintains backward compatibility while delegating to the centralized service.
 * 
 * DEPRECATED: New code should use DateTimeUtils from utils/datetimeUtils instead.
 */

// Import the new DateTimeUtils
import DateTimeUtils from './datetimeUtils';

export class TimezoneManager {
  
  /**
   * Detecta la zona horaria del navegador del usuario
   * 
   * DEPRECATED: Use DateTimeUtils.getUserTimezone() instead.
   */
  static getUserTimezone() {
    return DateTimeUtils.getUserTimezone();
  }
  
  /**
   * Obtiene información completa de zona horaria del usuario
   * 
   * DEPRECATED: Use DateTimeUtils.getUserTimezoneInfo() instead.
   */
  static getUserTimezoneInfo() {
    return DateTimeUtils.getUserTimezoneInfo();
  }
  
  /**
   * Formatea timestamp UTC para mostrar en zona horaria local del usuario
   * 
   * DEPRECATED: Use DateTimeUtils.formatForUser() instead.
   */
  static formatForUser(utcTimestamp, options = {}) {
    return DateTimeUtils.formatForUser(utcTimestamp, options);
  }
  
  /**
   * Convierte fecha local del usuario a UTC para enviar al backend
   * 
   * DEPRECATED: Use DateTimeUtils.toUTC() instead.
   */
  static toUTC(localDateTime) {
    return DateTimeUtils.toUTC(localDateTime);
  }
  
  /**
   * Agrega headers de zona horaria a una request
   * 
   * DEPRECATED: Use DateTimeUtils.addTimezoneHeaders() instead.
   */
  static addTimezoneHeaders(headers = {}) {
    return DateTimeUtils.addTimezoneHeaders(headers);
  }
  
  /**
   * Crea opciones para apiCall que incluyen zona horaria
   * 
   * DEPRECATED: Use DateTimeUtils.createApiCallOptions() instead.
   */
  static createApiCallOptions(options = {}) {
    return DateTimeUtils.createApiCallOptions(options);
  }
  
  /**
   * Muestra información debug de timezone en consola
   * 
   * DEPRECATED: Use DateTimeUtils.debugTimezone() instead.
   */
  static debugTimezone() {
    return DateTimeUtils.debugTimezone();
  }
  
  /**
   * Verifica si dos timestamps están en el mismo día local del usuario
   * 
   * DEPRECATED: Use DateTimeUtils.isSameDay() instead.
   */
  static isSameLocalDay(timestamp1, timestamp2) {
    return DateTimeUtils.isSameDay(timestamp1, timestamp2);
  }
  
  /**
   * Obtiene el inicio del día actual en UTC para el usuario
   * 
   * DEPRECATED: Use DateTimeUtils.getStartOfDayUTC() instead.
   */
  static getStartOfDayUTC() {
    return DateTimeUtils.getStartOfDayUTC();
  }
}

// ============================================
// FUNCIONES DE CONVENIENCIA
// ============================================

/**
 * Shortcut para obtener zona horaria del usuario
 */
export const getUserTimezone = () => TimezoneManager.getUserTimezone();

/**
 * Shortcut para formatear fecha para el usuario
 */
export const formatForUser = (timestamp, options = {}) => 
  TimezoneManager.formatForUser(timestamp, options);

/**
 * Shortcut para agregar headers de timezone
 */
export const addTimezoneHeaders = (headers = {}) => 
  TimezoneManager.addTimezoneHeaders(headers);

/**
 * Wrapper para apiCall que incluye automáticamente timezone headers
 * 
 * DEPRECATED: Use apiCallWithTimezone from datetimeUtils instead.
 */
export const apiCallWithTimezone = async (endpoint, method = 'GET', data = null, forceReauth = false, options = {}) => {
  // Delegate to DateTimeUtils
  const { apiCallWithTimezone: dtApiCall } = await import('./datetimeUtils');
  return dtApiCall(endpoint, method, data, forceReauth, options);
};

// ============================================
// AUTO-INICIALIZACIÓN (OPCIONAL)
// ============================================

// Mostrar información de timezone en desarrollo
if (process.env.NODE_ENV === 'development') {
  console.log('🌍 Timezone Utils loaded for:', TimezoneManager.getUserTimezone());
}

export default TimezoneManager; 